import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { Observer } from "gsap/dist/Observer";
import { ScrollToPlugin } from "gsap/dist/ScrollToPlugin";
import { VideoModalController } from "../modules/video-modal-controller";
import Swiper, { Navigation, Pagination, EffectFade, Thumbs, Parallax, Autoplay } from "swiper";
import "../../scss/layouts/blocks/swiper.scss";

Swiper.use([Navigation, Pagination, EffectFade, Thumbs, Parallax, Autoplay]);

gsap.registerPlugin(ScrollTrigger, Observer, ScrollToPlugin);

class SolutionScrollBlock {
	// Viewport and responsive behavior.
	static DESKTOP_BREAKPOINT = 1280;
	static VIEWPORT_TOGGLE_DELAY = 151;
	static INITIAL_SYNC_RETRIES = 10;
	static INITIAL_SYNC_RETRY_MS = 90;

	// Scroll scene length and per-section placement.
	static SECTION_SCROLL_RATIO = 0.95;
	static MIN_SECTION_SCROLL = 560;
	static MAX_SECTION_SCROLL = 1400;
	static SECTION_SCROLL_MULTIPLIER = 1.5;
	static SECTION_SCROLL_STEP_RATIO = 0.12;
	static SECTION_SCROLL_STEP_MIN = 60;
	static SECTION_SCROLL_STEP_MAX = 160;
	static HERO_SCROLL_STEP_RATIO = 0.25;
	static HERO_SCROLL_STEP_MAX = 200;
	static SECTION_PROGRESS_EPS = 0.0001;

	// Visibility, transitions, and animation timing.
	static VISIBILITY_HIDE_DELAY = 420;
	static LOTTIE_EXIT_DURATION = 650;
	static HERO_CTA_REVEAL_DELAY = 420;
	static HERO_CTA_HIDE_DELAY = 220;
	static HERO_CTA_HANDOFF_DELAY = 2100;
	static CTA_ENTER_DURATION_MS = 650;
	static CTA_FADE_DURATION_MS = 500;
	static TITLE_LINE_DELAY = 150;
	static TITLE_LINE_TOLERANCE = 2;
	static LIST_BASE_DELAY = 300;
	static LIST_ITEM_STEP = 120;
	static CONTENT_TITLE_DELAY = 100;
	static CONTENT_STEP_DELAY = 70;
	static CONTENT_LIST_MIN_STEP = 70;
	static HERO_LEFT_REVEAL_DELAY = 200;
	static HERO_FIRST_CONTENT_EXTRA = 260;
	static CONTENT_DEFAULT_DELAY = 90;

	// Parallax and pre-pin behavior.
	static PARALLAX_STRENGTH_EVEN = 36;
	static PARALLAX_STRENGTH_ODD = 24;
	static PREPIN_START_HOOK = 0.85;

	// Step scrolling and gesture handling.
	static SCROLL_FAILSAFE_MS = 700;
	static STEP_SCROLL_DURATION = 0.28;
	static STEP_SCROLL_EASE = "power2.out";
	static TRACKPAD_UNLOCK_DELAY_MS = 260;
	static OBSERVER_STOP_DELAY = 0.12;
	static OBSERVER_TOLERANCE = 2;
	static GESTURE_GAP_MS = 140;
	static REARM_MIN_MS = 140;
	static REARM_DELTA_Y = 10;
	static REARM_SPIKE_MULT = 1.6;
	static REARM_DELTA_Y_EDGE = 6;
	static REARM_SPIKE_MULT_EDGE = 1.25;
	static STEP_DELTA_RATIO = 0.14;
	static STEP_DELTA_MIN = 90;
	static STEP_DELTA_MAX = 220;
	static TRACKPAD_STEP_DELTA_RATIO = 0.07;
	static TRACKPAD_STEP_DELTA_MIN = 30;
	static TRACKPAD_STEP_DELTA_MAX = 140;
	static EDGE_STEP_DELTA = 45;
	static EDGE_EXIT_NUDGE_PX = 2;

	// Scene entry/exit buffers.
	static EXIT_BUFFER_RATIO = 0.35;
	static EXIT_BUFFER_MIN = 260;
	static EXIT_BUFFER_MAX = 900;
	static LAST_SLIDE_END_BUFFER_RATIO = 0.4;
	static LAST_SLIDE_END_BUFFER_MIN = 260;
	static LAST_SLIDE_END_BUFFER_MAX = 760;

	// Grid container vertical padding (padding-top / padding-bottom).
	// Config is selected by solution_page value (data-solution-page).
	//   minTop1920 — min padding-top when viewport >= 1920 px
	//   minTop1440 — min padding-top when viewport >= 1440 px
	//   minTop     — min padding-top when viewport <  1440 px
	//   extraTop   — extra offset added on top of the computed padding-top
	//   minBottom  — min padding-bottom
	static GRID_CONFIGS = {
		default: { minTop1920: 200, minTop1440: 200, minTop: 110, extraTop: 10, minBottom: 120 },
		finances: { minTop1920: 150, minTop1440: 140, minTop: 110, extraTop: 10, minBottom: 120 },
	};

	// Oil-only Lottie positioning under the sections list.
	static OIL_LOTTIE_TOP_GAP_PX = 0;
	static OIL_S2_LOTTIE_TOP_ADJUST_PX = -110;
	static OIL_S3_LOTTIE_TOP_ADJUST_PX = -40;

	// Lottie selectors whose top is computed as gridPaddingTop + leftCol height (life-sciences, manufacturing).
	static LIFE_SCIENCES_LOTTIE_SELECTOR = ".solutionScrollCarousel__decor-item--s1-life-sciences-lottie, .solutionScrollCarousel__decor-item--s2-life-sciences-lottie, .solutionScrollCarousel__decor-item--s3-life-sciences-lottie";
	static MANUFACTURING_LOTTIE_SELECTOR = ".solutionScrollCarousel__decor-item--s1-manufacturing-lottie, .solutionScrollCarousel__decor-item--s2-manufacturing-lottie, .solutionScrollCarousel__decor-item--s3-manufacturing-lottie";
	static GRID_TOP_LOTTIE_SELECTOR = `${SolutionScrollBlock.LIFE_SCIENCES_LOTTIE_SELECTOR}, ${SolutionScrollBlock.MANUFACTURING_LOTTIE_SELECTOR}`;

	constructor(root) {
		this.root = root;
		this.solutionPage = (root.dataset && root.dataset.solutionPage) || "";
		this.hasHero = Boolean(root.dataset && root.dataset.hasHero === "1");
		this.heroOffset = this.hasHero ? 1 : 0;
		this.desktopInitialized = false;
		this.lastDesktop = this.isDesktop();
		this.toggleTimer = null;
		this.observer = null;
		this.isTweening = false;
		this.deltaAccumulator = 0;
		this.gestureLocked = false;
		this.lastStepCompleteAt = 0;
		this.lastLockedWheelAbs = 0;
		this.pendingRelease = false;

		this.handleViewportToggle = this.handleViewportToggle.bind(this);
		this.handleResize = this.handleResize.bind(this);
		this.handlePageShow = this.handlePageShow.bind(this);
		this.handleBeforeUnload = this.handleBeforeUnload.bind(this);

		if (this.lastDesktop) {
			this.initDesktop();
		}

		window.addEventListener("resize", this.handleViewportToggle);
		window.addEventListener("orientationchange", this.handleViewportToggle);
		window.addEventListener("pageshow", this.handlePageShow);
		window.addEventListener("beforeunload", this.handleBeforeUnload);

		this.initVideoModals();
	}

	killInstance(propName, options = {}) {
		const instance = this[propName];
		if (!instance) {
			return;
		}
		const method = options.method || "kill";
		const args = Array.isArray(options.args) ? options.args : [];
		try {
			if (typeof instance[method] === "function") {
				instance[method](...args);
			}
		} catch (e) { }
		this[propName] = null;
	}

	killDelay(handle) {
		if (!handle) {
			return;
		}
		if (typeof handle.kill === "function") {
			try {
				handle.kill();
			} catch (e) { }
			return;
		}
		try {
			window.clearTimeout(handle);
		} catch (e) { }
	}

	delay(ms, cb) {
		const delayMs = Math.max(0, Number(ms) || 0);
		try {
			return gsap.delayedCall(delayMs / 1000, cb);
		} catch (e) {
			return window.setTimeout(cb, delayMs);
		}
	}

	isDesktop() {
		return window.innerWidth >= SolutionScrollBlock.DESKTOP_BREAKPOINT;
	}

	initDesktop() {
		if (this.desktopInitialized || !this.isDesktop()) {
			return;
		}

		this.cacheElements();
		if (!this.pinEl || !this.panels.length) {
			return;
		}

		this.resetRuntimeState();
		this.root.dataset.sscInit = "1";

		this.desktopInitialized = true;

		this.prefetchLottieAssets();

		this.initParallaxTrigger();
		this.initPrePinTrigger();
		this.initMainPinTrigger();
		this.setInitialHiddenState();
		this.scheduleInitialSync();
		this.scheduleGridOffsetUpdate();
		this.initOilLottieTopTracking();
		this.initGridTopLottieTracking();
		this.bindListItemNavigation();
		this.initObserver();

		window.addEventListener("resize", this.handleResize);
		window.addEventListener("orientationchange", this.handleResize);
	}

	destroyDesktop() {
		if (!this.desktopInitialized) {
			return;
		}

		if (this.gridOffsetRaf) {
			window.cancelAnimationFrame(this.gridOffsetRaf);
			this.gridOffsetRaf = null;
		}

		this.clearInitialSyncTimer();
		this.clearAllRevealTimers();
		this.clearInitialRevealTimer();
		this.hideHeroCta(0);
		this.clearHeroCtaRevealTimer();
		this.clearHeroCtaHideTimer();
		this.unbindListItemNavigation();
		this.clearScrollFailsafe();

		this.killInstance("mainTrigger", { method: "kill", args: [true] });
		this.killInstance("prePinTrigger", { method: "kill", args: [true] });
		this.killInstance("parallaxTrigger", { method: "kill", args: [true] });
		this.killInstance("observer", { method: "kill" });

		window.removeEventListener("resize", this.handleResize);
		window.removeEventListener("orientationchange", this.handleResize);

		this.destroyOilLottieTopTracking();
		this.destroyGridTopLottieTracking();

		this.resetAllLotties(this.root);
		if (this.panels && this.panels.length) {
			this.panels.forEach((p) => this.clearLottieWaitTimer(p));
		}
		if (this.decorPanels && this.decorPanels.length) {
			this.decorPanels.forEach((p) => this.clearLottieWaitTimer(p));
		}
		this.resetDomAfterDestroy();
		this.desktopInitialized = false;
		delete this.root.dataset.sscInit;
	}

	handleBeforeUnload() {
		if (!this.desktopInitialized) {
			return;
		}
		this.destroyDesktop();
	}

	handlePageShow(event) {
		if (!this.desktopInitialized && this.isDesktop()) {
			this.initDesktop();
			return;
		}
		if (!this.desktopInitialized) {
			return;
		}
		// Fresh loads are already synced at init; a repeat sync here re-runs the left
		// column reveal mid-scroll (visible flash), so only re-sync after a bfcache restore
		if (!event || !event.persisted) {
			return;
		}
		this.scheduleInitialSync();
	}

	// Warm the HTTP cache for lottie JSONs at init; otherwise the fetch starts only when a
	// slide activates and queues behind the page's images, leaving slides without animation
	prefetchLottieAssets() {
		if (this.lottiePrefetched) {
			return;
		}
		this.lottiePrefetched = true;
		this.root.querySelectorAll(".scrollCarouselLottie[data-animation-path]").forEach((el) => {
			const path = el.dataset.animationPath;
			if (!path) {
				return;
			}
			try {
				fetch(path, { priority: "low", credentials: "same-origin" }).catch(() => { });
			} catch (e) { }
		});
	}

	scheduleInitialSync() {
		this.initialSyncAttempt = 0;
		this.clearInitialSyncTimer();
		this.runInitialSyncAttempt(0);
	}

	clearInitialSyncTimer() {
		if (!this.initialSyncTimer) {
			return;
		}
		this.killDelay(this.initialSyncTimer);
		this.initialSyncTimer = null;
	}

	runInitialSyncAttempt(delayMs) {
		this.clearInitialSyncTimer();
		this.initialSyncTimer = this.delay(delayMs, () => {
			this.initialSyncTimer = null;
			if (!this.desktopInitialized || !this.mainTrigger) {
				return;
			}
			try {
				ScrollTrigger.refresh(true);
			} catch (e) { }
			const ok = this.syncInitialActiveState();
			if (ok) {
				return;
			}
			this.initialSyncAttempt += 1;
			if (this.initialSyncAttempt >= SolutionScrollBlock.INITIAL_SYNC_RETRIES) {
				return;
			}
			this.runInitialSyncAttempt(SolutionScrollBlock.INITIAL_SYNC_RETRY_MS);
		});
	}

	handleViewportToggle() {
		if (this.toggleTimer) {
			this.killDelay(this.toggleTimer);
		}
		const apply = () => {
			this.toggleTimer = null;
			const nowDesktop = this.isDesktop();
			if (nowDesktop && !this.desktopInitialized) {
				this.initDesktop();
			} else if (!nowDesktop && this.desktopInitialized) {
				this.destroyDesktop();
			}
			this.lastDesktop = nowDesktop;
		};
		this.toggleTimer = this.delay(SolutionScrollBlock.VIEWPORT_TOGGLE_DELAY, apply);
	}

	cacheElements() {
		const root = this.root;
		this.pinEl = root.querySelector(".solutionScrollCarousel__pin");
		this.gridEl = root.querySelector(".solutionScrollCarousel__grid");
		this.leftCol = root.querySelector(".solutionScrollCarousel__left");
		this.ctaRail = root.querySelector(".solutionScrollCarousel__cta-rail");
		this.overlayEl = root.querySelector(".solutionScrollCarousel__overlay");
		this.panels = Array.from(root.querySelectorAll(".js-panel"));
		this.decorPanels = Array.from(root.querySelectorAll(".js-panel-decor"));
		this.ctas = Array.from(root.querySelectorAll(".js-cta"));
		this.sectionsListEl = root.querySelector(".solutionScrollCarousel__sectionsList");
		this.listItems = Array.from(root.querySelectorAll(".solutionScrollCarousel__sectionsList-item"));
		this.heroPanel = this.hasHero ? root.querySelector(".solutionScrollCarousel__panel--hero") : null;
		this.heroBackground = this.hasHero ? root.querySelector(".solutionScrollCarousel__hero-bg") : null;
		this.heroCta = this.hasHero ? root.querySelector(".solutionScrollCarousel__cta--hero") : null;
		this.parallaxTargets = Array.from(root.querySelectorAll(".js-parallax"));
		this.oilLottieTopTargets = Array.from(root.querySelectorAll(".solutionScrollCarousel__decor-item--s2-oil-lottie, .solutionScrollCarousel__decor-item--s3-oil-lottie"));
		this.gridTopLottieTargets = Array.from(root.querySelectorAll(SolutionScrollBlock.GRID_TOP_LOTTIE_SELECTOR));
	}

	resetRuntimeState() {
		this.currentCtaIndex = this.hasHero ? 0 : -1;
		this.lastActiveIndex = -1;
		this.sceneStarted = false;
		this.sceneReleased = false;
		this.listItemBindings = [];
		this.fadeTimers = new WeakMap();
		this.ctaTimers = new WeakMap();
		if (!this.revealTimers) {
			this.revealTimers = new Map();
		} else {
			this.clearAllRevealTimers();
		}
		this.revealTimers.clear();

		if (this.gridOffsetRaf) {
			window.cancelAnimationFrame(this.gridOffsetRaf);
			this.gridOffsetRaf = null;
		}

		this.initialRevealRan = false;
		this.heroCtaRevealTimer = null;
		this.heroCtaHideTimer = null;
		this.heroCtaHoldActive = false;

		this.mainTrigger = null;
		this.prePinTrigger = null;
		this.parallaxTrigger = null;
		this.pendingScrollIndex = null;
		this.pendingScrollCheckTimer = null;
		this.isTweening = false;
		this.deltaAccumulator = 0;
		this.gestureLocked = false;
		this.lastWheelAt = 0;
		this.pendingStop = false;
		this.pendingStopAt = 0;
		this.lastInputIsTrackpad = false;
		this.lockedByStep = false;
		this.unlockTimer = null;
		this.lastStepCompleteAt = 0;
		this.lastLockedWheelAbs = 0;
		this.pendingRelease = false;
	}

	isTrackpadEvent(self) {
		const e = self && self.event ? self.event : null;
		return Boolean(e && e.deltaMode === 0);
	}

	clearUnlockTimer() {
		if (!this.unlockTimer) {
			return;
		}
		this.killDelay(this.unlockTimer);
		this.unlockTimer = null;
	}

	scheduleUnlock(delayMs) {
		this.clearUnlockTimer();
		const ms = Math.max(0, Number(delayMs) || 0);
		this.unlockTimer = this.delay(ms, () => {
			this.unlockTimer = null;
			this.gestureLocked = false;
			this.lockedByStep = false;
			this.deltaAccumulator = 0;
			this.lastLockedWheelAbs = 0;
		});
	}

	handleWheel(self) {
		const now = Date.now();
		const prevWheelAt = this.lastWheelAt || 0;
		const isNewGesture = !prevWheelAt || now - prevWheelAt > SolutionScrollBlock.GESTURE_GAP_MS;
		this.lastWheelAt = now;
		this.lastInputIsTrackpad = this.isTrackpadEvent(self);

		// On inertial trackpads, Observer's onStop can be delayed/suppressed by a long tail of events.
		// Unlocking on a *new* gesture prevents "dead" states without letting inertia trigger multi-steps.
		if (this.gestureLocked && this.lockedByStep && this.lastInputIsTrackpad && isNewGesture && !this.isTweening) {
			this.clearUnlockTimer();
			this.gestureLocked = false;
			this.lockedByStep = false;
			this.deltaAccumulator = 0;
			this.lastLockedWheelAbs = 0;
		}

		this.handleRearmWheel(self, { isNewGesture });
	}

	clearCtaTimer(el) {
		if (!el || !this.ctaTimers) {
			return;
		}
		const t = this.ctaTimers.get(el);
		if (!t) {
			return;
		}
		this.killDelay(t);
		this.ctaTimers.delete(el);
	}

	scheduleCtaTimer(el, delayMs, cb) {
		if (!el) {
			return null;
		}
		this.clearCtaTimer(el);
		const ms = Math.max(0, Number(delayMs) || 0);
		const handle = this.delay(ms, () => {
			try {
				cb();
			} catch (e) { }
			if (this.ctaTimers) {
				this.ctaTimers.delete(el);
			}
		});
		this.ctaTimers.set(el, handle);
		return handle;
	}

	resetDomAfterDestroy() {
		const root = this.root;
		if (this.overlayEl) {
			this.overlayEl.style.visibility = "";
			this.overlayEl.style.opacity = "";
		}
		if (this.gridEl) {
			this.gridEl.style.visibility = "";
			this.gridEl.style.opacity = "";
			this.gridEl.style.paddingTop = "";
			this.gridEl.style.paddingBottom = "";
		}
		if (this.heroBackground) {
			this.heroBackground.classList.remove("is-hidden");
		}
		root.classList.remove("is-fixed");
		root.classList.remove("is-released");
		root.classList.remove("is-hero-active");
		root.classList.remove("has-left-hero");
		root.classList.remove("is-resetting");

		if (this.parallaxTargets && this.parallaxTargets.length) {
			this.parallaxTargets.forEach((el) => {
				if (el && el.style) {
					el.style.transform = "";
				}
			});
		}

		if (this.decorPanels) {
			this.decorPanels.forEach((panel) => {
				panel.classList.remove("is-active");
				panel.style.visibility = "";
				panel.style.opacity = "";
			});
		}

		if (this.panels) {
			this.panels.forEach((panel) => {
				panel.classList.remove("is-active");
				panel.style.visibility = "";
				panel.style.opacity = "";
				delete panel.dataset.panelAnimated;
			});
		}

		if (this.ctas) {
			this.ctas.forEach((cta) => {
				cta.classList.remove("is-active");
				cta.classList.remove("is-entering");
				cta.classList.remove("is-fading");
			});
		}

		if (this.pinEl) {
			try {
				this.pinEl.style.height = "";
			} catch (e) { }
		}
	}

	initParallaxTrigger() {
		if (!this.parallaxTargets || !this.parallaxTargets.length) {
			return;
		}

		this.killInstance("parallaxTrigger", { method: "kill", args: [true] });

		this.parallaxTrigger = ScrollTrigger.create({
			trigger: this.root,
			start: "top top",
			end: () => `+=${this.getDuration()}`,
			onUpdate: (self) => {
				const centered = self.progress - 0.5;
				this.parallaxTargets.forEach((el, idx) => {
					if (!(el instanceof HTMLElement)) {
						return;
					}
					const fallback = idx % 2 === 0 ? SolutionScrollBlock.PARALLAX_STRENGTH_EVEN : SolutionScrollBlock.PARALLAX_STRENGTH_ODD;
					const strength = parseFloat(el.dataset.parallaxStrength || fallback);
					const invert = el.dataset.parallaxInvert === "1" || el.dataset.parallaxInvert === "true";
					const base = -centered * 2 * strength;
					const translate = Math.round(base) * (invert ? -1 : 1);
					el.style.transform = `translateY(${translate}px)`;
				});
			},
		});
	}

	initPrePinTrigger() {
		this.killInstance("prePinTrigger", { method: "kill", args: [true] });

		const hook = Math.round(SolutionScrollBlock.PREPIN_START_HOOK * 100);
		this.prePinTrigger = ScrollTrigger.create({
			trigger: this.root,
			start: `top ${hook}%`,
			end: "top top",
			onEnter: () => {
				if (this.sceneStarted || this.sceneReleased) {
					return;
				}
				this.fadeElement(this.gridEl, true);
				const heroBlocking = this.hasHero && this.root.classList.contains("is-hero-active") && !this.root.classList.contains("has-left-hero");
				if (heroBlocking) {
					return;
				}
				this.fadeElement(this.overlayEl, true);
				const firstDecor = this.decorPanels && this.decorPanels[0];
				if (firstDecor) {
					firstDecor.classList.add("is-active");
					firstDecor.style.visibility = "visible";
					firstDecor.style.opacity = "1";
					this.revealChildren(firstDecor);
					this.restartLotties(firstDecor);
				}
			},
			onLeaveBack: () => {
				if (this.sceneStarted || this.sceneReleased) {
					return;
				}
				this.fadeElement(this.overlayEl, false);
				this.fadeElement(this.gridEl, false);
				const firstDecor = this.decorPanels && this.decorPanels[0];
				if (firstDecor) {
					firstDecor.classList.remove("is-active");
					firstDecor.style.visibility = "hidden";
					firstDecor.style.opacity = "0";
				}
			},
		});
	}

	initMainPinTrigger() {
		this.killInstance("mainTrigger", { method: "kill", args: [true] });

		const panelCount = this.panels.length;
		const snapDivisions = Math.max(1, panelCount);

		this.mainTrigger = ScrollTrigger.create({
			trigger: this.root,
			start: "top top",
			end: () => `+=${this.getDuration() + this.getExitBuffer()}`,
			pin: this.pinEl,
			pinSpacing: true,
			anticipatePin: 1,
			invalidateOnRefresh: true,
			onEnter: () => {
				this.sceneStarted = true;
				this.sceneReleased = false;
				this.root.classList.add("is-fixed");
				this.root.classList.remove("is-released");
				this.fadeElement(this.overlayEl, true);
				this.fadeElement(this.gridEl, true);
				this.enableObserver();

				if (!this.hasHero) {
					this.runInitialReveal();
				}

				const initialIndex = this.lastActiveIndex > -1 ? this.lastActiveIndex : 0;
				this.setActive(initialIndex, true);
			},
			onLeave: () => {
				this.sceneStarted = false;
				this.sceneReleased = true;
				this.root.classList.remove("is-fixed");
				this.disableObserver();
				const lastIndex = this.panels.length - 1;
				this.setActive(lastIndex, true);
				if (this.isTweening) {
					this.pendingRelease = true;
				} else {
					this.applyReleasedState();
				}
			},
			onEnterBack: () => {
				this.sceneStarted = true;
				this.sceneReleased = false;
				this.root.classList.add("is-fixed");
				this.root.classList.remove("is-released");
				this.fadeElement(this.overlayEl, true);
				this.fadeElement(this.gridEl, true);
				this.enableObserver();
			},
			onLeaveBack: () => {
				this.sceneStarted = false;
				this.sceneReleased = false;
				this.root.classList.remove("is-fixed");
				this.root.classList.remove("is-released");
				this.disableObserver();
				this.applyTopResetState();
			},
		});
	}

	applyReleasedState() {
		this.pendingRelease = false;
		this.root.classList.add("is-released");
		this.fadeElement(this.overlayEl, true);
		this.fadeElement(this.gridEl, true);
	}

	handleResize() {
		if (!this.desktopInitialized) {
			return;
		}
		try {
			ScrollTrigger.refresh(true);
		} catch (e) { }
		this.scheduleGridOffsetUpdate();
		this.scheduleOilLottieTopUpdate();
	}

	setActive(index, force = false) {
		if (!force && !this.sceneStarted && !this.sceneReleased) {
			return;
		}
		const maximumIndex = Math.max(0, this.panels.length - 1);
		const activeIndex = this.clamp(index, 0, maximumIndex);
		const prevIndex = this.lastActiveIndex;
		if (prevIndex === activeIndex) {
			return;
		}
		const direction = prevIndex > -1 && activeIndex < prevIndex ? "reverse" : "forward";
		if (prevIndex > -1 && prevIndex !== activeIndex) {
			const prevPanel = this.findByIndex(this.panels, prevIndex);
			const prevDecor = this.findByIndex(this.decorPanels, prevIndex);
			if (prevPanel) {
				this.animateLottieExit(prevPanel, direction);
			}
			if (prevDecor) {
				this.animateLottieExit(prevDecor, direction);
			}
		}

		const heroIsActive = this.hasHero && activeIndex === 0;
		const firstContentIndex = this.hasHero ? this.heroOffset : 0;
		const enteringFromHero = this.hasHero && prevIndex === 0 && activeIndex === firstContentIndex;
		if (!this.sceneStarted && !this.sceneReleased) {
			this.runInitialReveal(0);
		} else if (enteringFromHero) {
			this.runInitialReveal(SolutionScrollBlock.HERO_LEFT_REVEAL_DELAY);
		}
		this.root.classList.toggle("is-hero-active", heroIsActive);
		if (this.hasHero) {
			if (heroIsActive) {
				this.root.classList.remove("has-left-hero");
			} else {
				this.root.classList.add("has-left-hero");
			}
		}
		if (this.heroBackground) {
			this.heroBackground.classList.toggle("is-hidden", !heroIsActive);
		}
		if (heroIsActive) {
			this.fadeElement(this.overlayEl, false, 0);
		} else {
			this.fadeElement(this.overlayEl, true);
		}

		if (this.heroCta) {
			const rootColor = this.heroPanel ? this.heroPanel.dataset.color || this.heroPanel.dataset.ctaColor || "green" : "green";
			this.heroCta.dataset.ctaColor = rootColor;
		}

		this.panels.forEach((panel) => {
			const panelIndex = this.getElementIndex(panel);
			const isActive = panelIndex === activeIndex;
			panel.classList.toggle("is-active", Boolean(isActive));
			if (isActive) {
				panel.style.visibility = "visible";
				panel.style.opacity = "";
				if (this.hasHero && panelIndex === 0) {
					this.animateHeroPanel();
				} else {
					const base = activeIndex === firstContentIndex ? this.listItems.length * SolutionScrollBlock.LIST_ITEM_STEP + SolutionScrollBlock.HERO_FIRST_CONTENT_EXTRA : SolutionScrollBlock.CONTENT_DEFAULT_DELAY;
					this.animatePanelContent(panel, base, force);
				}
				this.restartLotties(panel);
			} else if (!this.hasHero || panelIndex !== 0) {
				this.resetPanelContent(panel);
				panel.style.visibility = "hidden";
				panel.style.opacity = "0";
			}
		});

		this.decorPanels.forEach((panel) => {
			const panelIndex = this.getElementIndex(panel);
			const isActive = panelIndex === activeIndex;
			panel.classList.toggle("is-active", Boolean(isActive));
			if (isActive) {
				panel.style.visibility = "visible";
				panel.style.opacity = "";
				this.revealChildren(panel);
				this.restartLotties(panel);
			} else {
				panel.style.visibility = "hidden";
				panel.style.opacity = "0";
			}
		});

		this.listItems.forEach((item) => {
			const liIndex = Number(item.dataset.slideIndex);
			const matches = Number.isFinite(liIndex) && liIndex === activeIndex;
			item.classList.toggle("is-active", matches);
		});

		let heroCtaTransitionHandled = false;
		if (this.ctas.length) {
			const prev = this.findByIndex(this.ctas, this.currentCtaIndex);
			const next = this.findByIndex(this.ctas, activeIndex);
			let pendingHeroHide = false;
			if (prev) {
				this.clearCtaTimer(prev);
				const prevIsHero = this.hasHero && this.currentCtaIndex === 0;
				const shouldHoldHero = prevIsHero && activeIndex !== 0 && !force;
				if (force) {
					prev.classList.remove("is-active");
					prev.classList.remove("is-fading");
					prev.classList.remove("is-entering");
				} else if (this.currentCtaIndex !== activeIndex && !shouldHoldHero) {
					prev.classList.add("is-fading");
					this.scheduleCtaTimer(prev, SolutionScrollBlock.CTA_FADE_DURATION_MS, () => {
						prev.classList.remove("is-active");
						prev.classList.remove("is-fading");
					});
				}
				if (this.hasHero && this.currentCtaIndex === 0 && activeIndex !== 0 && !force && next && !heroCtaTransitionHandled) {
					pendingHeroHide = true;
					this.heroCtaHoldActive = true;
					heroCtaTransitionHandled = true;
				}
			}
			if (next) {
				this.clearCtaTimer(next);
				next.dataset.ctaColor = next.dataset.color || next.dataset.ctaColor || "green";
				if (force || this.currentCtaIndex === -1 || this.currentCtaIndex === activeIndex) {
					next.classList.add("is-active");
					next.classList.remove("is-entering");
				} else {
					next.classList.add("is-entering");
					void next.offsetHeight;
					this.scheduleCtaTimer(next, SolutionScrollBlock.CTA_ENTER_DURATION_MS, () => {
						next.classList.add("is-active");
						next.classList.remove("is-entering");
					});
				}
				if (pendingHeroHide) {
					this.hideHeroCta(SolutionScrollBlock.HERO_CTA_HANDOFF_DELAY);
				}
			}
			this.currentCtaIndex = activeIndex;
		}

		if (this.hasHero) {
			if (activeIndex === 0) {
				this.showHeroCta();
			} else if (!heroCtaTransitionHandled) {
				this.hideHeroCta(SolutionScrollBlock.HERO_CTA_HIDE_DELAY);
			}
		}

		this.lastActiveIndex = activeIndex;
		this.scheduleGridOffsetUpdate();
		if (this.pendingScrollIndex !== null && this.pendingScrollIndex === activeIndex) {
			this.clearScrollFailsafe();
			this.pendingScrollIndex = null;
		}
	}

	animatePanelContent(panelEl, baseDelay = 0, force = false) {
		if (!panelEl || (panelEl.dataset.panelAnimated === "1" && !force)) {
			return;
		}
		const elements = [];
		const title = panelEl.querySelector(".solutionScrollCarousel__content-title");
		if (title) {
			elements.push({ el: title, delay: baseDelay });
		}
		let delay = baseDelay + SolutionScrollBlock.CONTENT_TITLE_DELAY;
		const contentText = panelEl.querySelector(".solutionScrollCarousel__content-text");
		if (contentText) {
			Array.from(contentText.children).forEach((child) => {
				if (!(child instanceof HTMLElement)) {
					return;
				}
				elements.push({ el: child, delay });
				delay += SolutionScrollBlock.CONTENT_STEP_DELAY;
			});
			Array.from(contentText.querySelectorAll(":scope > ul > li")).forEach((li) => {
				elements.push({ el: li, delay });
				delay += Math.max(SolutionScrollBlock.CONTENT_LIST_MIN_STEP, Math.round(SolutionScrollBlock.CONTENT_STEP_DELAY * 0.8));
			});
		}
		elements.forEach(({ el, delay: entryDelay }) => {
			this.clearElementReveal(el);
			this.scheduleElementReveal(el, entryDelay);
		});
		panelEl.dataset.panelAnimated = "1";
	}

	resetPanelContent(panelEl) {
		if (!panelEl) {
			return;
		}
		delete panelEl.dataset.panelAnimated;
		const title = panelEl.querySelector(".solutionScrollCarousel__content-title");
		if (title) {
			this.clearElementReveal(title);
		}
		const contentText = panelEl.querySelector(".solutionScrollCarousel__content-text");
		if (contentText) {
			Array.from(contentText.children).forEach((child) => {
				if (!(child instanceof HTMLElement)) {
					return;
				}
				this.clearElementReveal(child);
			});
			Array.from(contentText.querySelectorAll(":scope > ul > li")).forEach((li) => this.clearElementReveal(li));
		}
	}

	animateHeroPanel() {
		if (!this.heroPanel || this.heroPanel.dataset.heroAnimated === "1") {
			return;
		}
		const title = this.heroPanel.querySelector(".solutionScrollCarousel__hero-title");
		if (title) {
			this.buildTitleLines(title);
			title.style.visibility = "visible";
			this.playTitleLines(title, SolutionScrollBlock.TITLE_LINE_DELAY, 0);
		}
		this.heroPanel.dataset.heroAnimated = "1";
	}

	animateList(items, baseDelay = SolutionScrollBlock.LIST_BASE_DELAY, step = SolutionScrollBlock.LIST_ITEM_STEP) {
		items.forEach((el, index) => {
			this.clearElementReveal(el);
			const delay = baseDelay + index * step;
			this.scheduleElementReveal(el, delay);
		});
		return baseDelay + items.length * step + 200;
	}

	revealChildren(scope) {
		if (!scope) {
			return;
		}
		const items = Array.from(scope.querySelectorAll(".js-fade-item"));
		if (!items.length) {
			return;
		}

		const STEP_S = 0.08;

		if (scope._sscRevealCalls && Array.isArray(scope._sscRevealCalls)) {
			scope._sscRevealCalls.forEach((c) => this.killDelay(c));
		}
		scope._sscRevealCalls = [];

		// First item immediately, rest staggered via delay() (killable).
		items.forEach((el, idx) => {
			if (!el) return;
			if (idx === 0) {
				try {
					el.classList.add("is-visible");
				} catch (e) { }
				return;
			}
			const call = this.delay(idx * STEP_S * 1000, () => {
				try {
					el.classList.add("is-visible");
				} catch (e) { }
			});
			scope._sscRevealCalls.push(call);
		});
	}

	fadeElement(el, show, delay = SolutionScrollBlock.VISIBILITY_HIDE_DELAY) {
		if (!el) {
			return;
		}
		if (el === this.overlayEl && this.hasHero) {
			const heroBlockingOverlay = this.root.classList.contains("is-hero-active") && !this.root.classList.contains("has-left-hero");
			if (heroBlockingOverlay && show) {
				el.style.opacity = "0";
				el.style.visibility = "hidden";
				return;
			}
		}
		const existing = this.fadeTimers.get(el);
		if (existing) {
			this.killDelay(existing);
			this.fadeTimers.delete(el);
		}
		if (show) {
			el.style.visibility = "visible";
			el.style.opacity = "1";
			return;
		}
		el.style.opacity = "0";
		if (delay <= 0) {
			el.style.visibility = "hidden";
			return;
		}
		const handle = this.delay(delay, () => {
			el.style.visibility = "hidden";
			this.fadeTimers.delete(el);
		});
		this.fadeTimers.set(el, handle);
	}

	clearHeroCtaRevealTimer() {
		if (this.heroCtaRevealTimer) {
			this.killDelay(this.heroCtaRevealTimer);
			this.heroCtaRevealTimer = null;
		}
	}

	clearHeroCtaHideTimer() {
		if (this.heroCtaHideTimer) {
			this.killDelay(this.heroCtaHideTimer);
			this.heroCtaHideTimer = null;
		}
	}

	clearInitialRevealTimer() {
		if (!this.initialRevealTimer) {
			return;
		}
		this.killDelay(this.initialRevealTimer);
		this.initialRevealTimer = null;
	}

	hideHeroCta(delay = 0) {
		if (!this.heroCta) {
			return;
		}
		this.clearHeroCtaRevealTimer();
		this.clearHeroCtaHideTimer();
		const apply = () => {
			this.heroCta.classList.remove("is-ready");
			this.heroCta.classList.remove("is-entering");
			this.heroCta.classList.remove("is-fading");
			if (!this.root.classList.contains("is-hero-active")) {
				this.heroCta.classList.remove("is-active");
			}
			this.heroCtaHoldActive = false;
			delete this.heroCta.dataset.heroCtaReady;
			this.heroCtaHideTimer = null;
		};
		if (delay > 0) {
			this.heroCtaHideTimer = this.delay(delay, apply);
		} else {
			apply();
		}
	}

	showHeroCta(delay = SolutionScrollBlock.HERO_CTA_REVEAL_DELAY) {
		if (!this.heroCta) {
			return;
		}
		this.clearHeroCtaHideTimer();
		this.heroCtaHoldActive = false;
		if (this.heroCta.dataset.heroCtaReady === "1") {
			this.heroCta.classList.add("is-ready");
			return;
		}
		this.clearHeroCtaRevealTimer();
		const apply = () => {
			if (!this.heroCta) {
				return;
			}
			this.heroCta.classList.add("is-ready");
			this.heroCta.dataset.heroCtaReady = "1";
			this.heroCtaRevealTimer = null;
		};
		this.heroCtaRevealTimer = this.delay(delay, apply);
	}

	runInitialReveal(startDelay = 0) {
		if (this.initialRevealRan) {
			return;
		}
		this.initialRevealRan = true;
		this.clearInitialRevealTimer();
		const exec = () => {
			const titleEl = this.root.querySelector(".solutionScrollCarousel__title");
			this.buildTitleLines(titleEl);
			if (titleEl) {
				titleEl.style.visibility = "visible";
			}
			const titleDuration = this.playTitleLines(titleEl, SolutionScrollBlock.TITLE_LINE_DELAY, 0);
			this.animateList(this.listItems, Math.max(200, titleDuration - 500), SolutionScrollBlock.LIST_ITEM_STEP);
			this.initialRevealTimer = null;
		};
		if (startDelay > 0) {
			try {
				this.initialRevealTimer = gsap.delayedCall(Math.max(0, startDelay) / 1000, exec);
			} catch (e) {
				this.initialRevealTimer = window.setTimeout(exec, startDelay);
			}
		} else {
			exec();
		}
	}

	resetInitialReveal() {
		this.initialRevealRan = false;
		this.clearInitialRevealTimer();
		const titleEl = this.root.querySelector(".solutionScrollCarousel__title");
		if (titleEl) {
			const lines = Array.from(titleEl.querySelectorAll(".line"));
			try {
				gsap.killTweensOf(lines);
				gsap.set(lines, { yPercent: 100 });
			} catch (e) { }
			titleEl.style.visibility = "hidden";
		}
		this.listItems.forEach((item) => this.clearElementReveal(item));
		this.panels.forEach((panel) => this.resetPanelContent(panel));
	}

	setInitialHiddenState() {
		this.fadeElement(this.overlayEl, false, 0);
		if (this.hasHero) {
			this.fadeElement(this.gridEl, true, 0);
			this.root.classList.add("is-hero-active");
			this.root.classList.remove("has-left-hero");
			if (this.heroBackground) {
				this.heroBackground.classList.remove("is-hidden");
			}
		} else {
			this.fadeElement(this.gridEl, false, 0);
			this.runInitialReveal();
		}
		this.hideHeroCta(0);
		this.decorPanels.forEach((panel) => {
			panel.style.visibility = "hidden";
			panel.style.opacity = "0";
		});
		this.panels.forEach((panel) => {
			const isHeroPanel = this.hasHero && panel === this.heroPanel;
			if (isHeroPanel) {
				panel.classList.add("is-active");
				panel.style.visibility = "visible";
				panel.style.opacity = "1";
				if (this.heroPanel) {
					delete this.heroPanel.dataset.heroAnimated;
				}
				this.animateHeroPanel();
			} else {
				panel.classList.remove("is-active");
				this.resetPanelContent(panel);
				panel.style.visibility = "hidden";
				panel.style.opacity = "0";
			}
		});
		const titleEl = this.root.querySelector(".solutionScrollCarousel__title");
		if (titleEl) {
			titleEl.style.visibility = "hidden";
		}
		this.listItems.forEach((item) => this.clearElementReveal(item));
		if (this.ctas.length) {
			this.ctas.forEach((cta) => {
				const ctaIndex = this.getElementIndex(cta);
				const shouldBeActive = this.hasHero && ctaIndex === 0;
				cta.classList.toggle("is-active", Boolean(shouldBeActive));
				cta.classList.remove("is-entering");
				cta.classList.remove("is-fading");
			});
		}

		if (this.hasHero && this.heroCta) {
			const rootColor = this.heroPanel ? this.heroPanel.dataset.color || this.heroPanel.dataset.ctaColor || "green" : "green";
			this.heroCta.dataset.ctaColor = rootColor;
			this.showHeroCta();
		}
	}

	syncInitialActiveState() {
		if (!this.mainTrigger || !this.panels.length) {
			return false;
		}
		const startOffset = this.getSceneStartOffset();
		const totalDuration = this.getDurationPx();
		const contentDuration = this.getDuration();
		if (!Number.isFinite(startOffset) || !Number.isFinite(totalDuration) || totalDuration <= 0 || !Number.isFinite(contentDuration) || contentDuration <= 0) {
			return false;
		}
		const currentY = this.getScrollY();
		if (currentY < startOffset) {
			return false;
		}
		const local = this.clamp(currentY - startOffset, 0, totalDuration - SolutionScrollBlock.SECTION_PROGRESS_EPS);
		const idx = local >= contentDuration ? this.panels.length - 1 : Math.min(this.panels.length - 1, Math.floor(local / (contentDuration / this.panels.length)));
		this.sceneStarted = currentY >= startOffset && currentY < startOffset + totalDuration;
		this.sceneReleased = currentY >= startOffset + totalDuration;
		this.root.classList.toggle("is-hero-active", idx === 0);
		this.root.classList.toggle("has-left-hero", idx !== 0);
		const forceDelay = idx === 0 ? 0 : 200;
		this.setActive(idx, true);
		// Don't reveal the left column while the hero slide is active (prevents title "flash" before first slide).
		if (!this.hasHero || idx !== 0) {
			this.forceRevealLeftColumn(forceDelay);
		}
		return true;
	}

	forceRevealLeftColumn(delay = 0) {
		if (this.hasHero && this.root.classList.contains("is-hero-active") && !this.root.classList.contains("has-left-hero")) {
			return;
		}
		const titleEl = this.root.querySelector(".solutionScrollCarousel__title");
		if (titleEl) {
			this.buildTitleLines(titleEl);
			titleEl.style.visibility = "visible";
			this.playTitleLines(titleEl, SolutionScrollBlock.TITLE_LINE_DELAY, delay);
		}
		this.animateList(this.listItems, delay, SolutionScrollBlock.LIST_ITEM_STEP);
	}

	applyTopResetState() {
		this.root.classList.remove("is-fixed");
		this.root.classList.remove("is-released");
		this.root.classList.remove("is-resetting");
		if (this.hasHero) {
			this.root.classList.add("is-hero-active");
			this.root.classList.remove("has-left-hero");
			this.hideHeroCta(0);
			if (this.heroBackground) {
				this.heroBackground.classList.remove("is-hidden");
			}
		} else {
			this.root.classList.remove("is-hero-active");
		}
		this.sceneStarted = false;
		this.sceneReleased = false;
		this.fadeElement(this.overlayEl, false, SolutionScrollBlock.VISIBILITY_HIDE_DELAY);
		if (this.hasHero) {
			this.fadeElement(this.gridEl, true, 0);
		} else {
			this.fadeElement(this.gridEl, false, SolutionScrollBlock.VISIBILITY_HIDE_DELAY);
		}
		this.resetInitialReveal();
		this.decorPanels.forEach((panel) => {
			panel.classList.remove("is-active");
			panel.style.visibility = "hidden";
			panel.style.opacity = "0";
		});
		this.panels.forEach((panel) => {
			panel.classList.remove("is-active");
			const isHeroPanel = this.hasHero && panel === this.heroPanel;
			if (isHeroPanel) {
				panel.classList.add("is-active");
				panel.style.visibility = "visible";
				panel.style.opacity = "1";
			} else {
				panel.style.visibility = "hidden";
				panel.style.opacity = "0";
			}
		});
		if (this.gridEl) {
			this.gridEl.style.paddingTop = "";
			this.gridEl.style.paddingBottom = "";
		}
		this.lastActiveIndex = -1;
		this.currentCtaIndex = this.hasHero ? 0 : -1;
		if (this.ctas.length) {
			this.ctas.forEach((cta) => {
				const ctaIndex = this.getElementIndex(cta);
				const shouldBeActive = this.hasHero && ctaIndex === 0;
				cta.classList.toggle("is-active", Boolean(shouldBeActive));
				cta.classList.remove("is-entering");
				cta.classList.remove("is-fading");
			});
		}
	}

	initObserver() {
		if (this.observer) {
			try {
				this.observer.kill();
			} catch (e) { }
			this.observer = null;
		}
		this.observer = Observer.create({
			target: window,
			type: "wheel,touch",
			allowClicks: true,
			preventDefault: true,
			tolerance: SolutionScrollBlock.OBSERVER_TOLERANCE,
			onStopDelay: SolutionScrollBlock.OBSERVER_STOP_DELAY,
			onChangeY: (self) => this.handleDelta(self),
			onStop: () => this.handleGestureStop(),
			onWheel: (self) => this.handleWheel(self),
		});
		this.observer.disable();
	}

	handleGestureStop() {
		if (this.isTweening) {
			this.pendingStop = true;
			this.pendingStopAt = Date.now();
			return;
		}
		if (this.gestureLocked && this.lockedByStep && this.lastInputIsTrackpad) {
			this.scheduleUnlock(SolutionScrollBlock.TRACKPAD_UNLOCK_DELAY_MS);
			return;
		}
		this.clearUnlockTimer();
		this.gestureLocked = false;
		this.lockedByStep = false;
		this.deltaAccumulator = 0;
		this.lastLockedWheelAbs = 0;
		this.pendingStop = false;
		this.pendingStopAt = 0;
	}

	handleRearmWheel(self, meta = {}) {
		if (!self || this.isTweening) {
			return;
		}
		if (!this.gestureLocked) {
			return;
		}
		const currentIndex = Number.isFinite(this.lastActiveIndex) && this.lastActiveIndex > -1 ? this.lastActiveIndex : 0;
		const lastIndex = Math.max(0, this.panels.length - 1);
		const now = Date.now();
		if (now - (this.lastStepCompleteAt || 0) < SolutionScrollBlock.REARM_MIN_MS) {
			return;
		}
		const dy = Number(self.deltaY || 0);
		const absDy = Number.isFinite(dy) ? Math.abs(dy) : 0;
		const edgeOutward = (currentIndex >= lastIndex && dy > 0) || (currentIndex <= 0 && dy < 0);
		const minDelta = edgeOutward ? SolutionScrollBlock.REARM_DELTA_Y_EDGE : SolutionScrollBlock.REARM_DELTA_Y;
		if (!absDy || absDy < minDelta) {
			return;
		}

		// Trackpad inertia generally decays; a real new gesture usually shows a noticeable delta spike.
		const prevAbs = this.lastLockedWheelAbs || absDy;
		this.lastLockedWheelAbs = absDy;
		let spikeMult = edgeOutward ? SolutionScrollBlock.REARM_SPIKE_MULT_EDGE : SolutionScrollBlock.REARM_SPIKE_MULT;
		if (this.lastInputIsTrackpad) {
			spikeMult = Math.min(spikeMult, 1.25);
		}
		if (absDy < prevAbs * spikeMult) {
			return;
		}

		this.gestureLocked = false;
		this.deltaAccumulator = 0;
		this.lastLockedWheelAbs = 0;
	}

	enableObserver() {
		if (this.observer) {
			this.observer.enable();
		}
	}

	disableObserver() {
		if (this.observer) {
			this.observer.disable();
		}
		this.isTweening = false;
		this.deltaAccumulator = 0;
		this.gestureLocked = false;
	}

	handleDelta(self) {
		if (!self || !this.mainTrigger) {
			return;
		}
		this.lastWheelAt = Date.now();
		this.lastInputIsTrackpad = this.isTrackpadEvent(self);
		if (!this.sceneStarted || this.sceneReleased) {
			return;
		}
		if (this.gestureLocked) {
			return;
		}
		if (this.isTweening) {
			return;
		}
		const currentIndex = Number.isFinite(this.lastActiveIndex) && this.lastActiveIndex > -1 ? this.lastActiveIndex : 0;
		const lastIndex = Math.max(0, this.panels.length - 1);
		const dy = Number(self.deltaY || 0);
		if (!Number.isFinite(dy) || dy === 0) {
			return;
		}
		this.deltaAccumulator += dy;
		const acc = this.deltaAccumulator;
		let threshold = this.getStepThreshold();
		if (currentIndex >= lastIndex && acc > 0) {
			threshold = Math.min(threshold, SolutionScrollBlock.EDGE_STEP_DELTA);
		} else if (currentIndex <= 0 && acc < 0) {
			threshold = Math.min(threshold, SolutionScrollBlock.EDGE_STEP_DELTA);
		}
		if (Math.abs(acc) < threshold) {
			return;
		}
		const direction = acc > 0 ? 1 : -1;
		this.deltaAccumulator = 0;
		this.gestureLocked = true;
		this.lockedByStep = true;
		this.clearUnlockTimer();
		this.stepOnce(direction);
	}

	getStepThreshold() {
		if (this.lastInputIsTrackpad) {
			const base = Math.round(this.getViewportHeight() * SolutionScrollBlock.TRACKPAD_STEP_DELTA_RATIO);
			return this.clamp(base, SolutionScrollBlock.TRACKPAD_STEP_DELTA_MIN, SolutionScrollBlock.TRACKPAD_STEP_DELTA_MAX);
		}
		const base = Math.round(this.getViewportHeight() * SolutionScrollBlock.STEP_DELTA_RATIO);
		return this.clamp(base, SolutionScrollBlock.STEP_DELTA_MIN, SolutionScrollBlock.STEP_DELTA_MAX);
	}

	stepOnce(direction) {
		const currentIndex = Number.isFinite(this.lastActiveIndex) && this.lastActiveIndex > -1 ? this.lastActiveIndex : 0;
		const lastIndex = Math.max(0, this.panels.length - 1);
		if (direction > 0 && currentIndex >= lastIndex) {
			const rawEndY = this.mainTrigger ? Number(this.mainTrigger.end) : null;
			const endY = Number.isFinite(rawEndY) ? Math.round(rawEndY + SolutionScrollBlock.EDGE_EXIT_NUDGE_PX) : null;
			this.scrollToY(Number.isFinite(endY) ? endY : this.getScrollY(), null);
			return;
		}
		if (direction < 0 && currentIndex <= 0) {
			const rawStartY = this.mainTrigger ? Number(this.mainTrigger.start) : null;
			const startY = Number.isFinite(rawStartY) ? Math.round(rawStartY - SolutionScrollBlock.EDGE_EXIT_NUDGE_PX) : null;
			this.scrollToY(Number.isFinite(startY) ? startY : this.getScrollY(), null);
			return;
		}
		const nextIndex = this.clamp(currentIndex + (direction > 0 ? 1 : -1), 0, lastIndex);
		this.scrollToIndex(nextIndex);
	}

	scrollToIndex(index) {
		const y = this.getScrollYForIndex(index);
		this.setActive(index, true);
		this.scrollToY(y, index);
	}

	scrollToY(targetY, targetIndex) {
		this.isTweening = true;
		gsap.to(window, {
			scrollTo: { y: targetY, autoKill: false },
			duration: SolutionScrollBlock.STEP_SCROLL_DURATION,
			ease: SolutionScrollBlock.STEP_SCROLL_EASE,
			overwrite: true,
			onComplete: () => {
				this.isTweening = false;
				this.lastStepCompleteAt = Date.now();
				if (this.pendingStop) {
					const stopAt = this.pendingStopAt || 0;
					const wheelAt = this.lastWheelAt || 0;
					this.pendingStop = false;
					this.pendingStopAt = 0;
					if (wheelAt <= stopAt) {
						this.handleGestureStop();
					}
				}
				if (this.pendingRelease) {
					requestAnimationFrame(() => this.applyReleasedState());
				}
				if (Number.isFinite(targetIndex)) {
					this.pendingScrollIndex = null;
				}
			},
		});
	}

	getScrollYForIndex(targetIndex) {
		const startOffset = this.getSceneStartOffset();
		const duration = this.getDuration();
		if (!Number.isFinite(startOffset) || !Number.isFinite(duration) || duration <= 0) {
			return this.getScrollY();
		}
		const totalSections = Math.max(1, this.panels.length);
		const sectionSpan = duration / totalSections;
		const maxY = startOffset + duration;
		let targetY = startOffset + sectionSpan * this.clamp(targetIndex, 0, totalSections - 1);
		if (targetIndex >= totalSections - 1) {
			const buffer = this.clamp(Math.round(sectionSpan * SolutionScrollBlock.LAST_SLIDE_END_BUFFER_RATIO), SolutionScrollBlock.LAST_SLIDE_END_BUFFER_MIN, SolutionScrollBlock.LAST_SLIDE_END_BUFFER_MAX);
			targetY = this.clamp(maxY - buffer, startOffset, maxY);
		} else {
			const baseStep = this.clamp(sectionSpan * SolutionScrollBlock.SECTION_SCROLL_STEP_RATIO, SolutionScrollBlock.SECTION_SCROLL_STEP_MIN, SolutionScrollBlock.SECTION_SCROLL_STEP_MAX);
			if (targetIndex === 1 && this.hasHero) {
				const heroStep = this.clamp(sectionSpan * SolutionScrollBlock.HERO_SCROLL_STEP_RATIO, baseStep, SolutionScrollBlock.HERO_SCROLL_STEP_MAX);
				targetY = this.clamp(targetY + heroStep, startOffset, maxY);
			} else {
				targetY = this.clamp(targetY + baseStep, startOffset, maxY);
			}
		}
		return targetY;
	}

	bindListItemNavigation() {
		this.unbindListItemNavigation();
		this.listItems.forEach((item, index) => {
			if (!(item instanceof HTMLElement)) {
				return;
			}
			const slideIndex = this.heroOffset + index;
			item.dataset.sectionIndex = String(index);
			item.dataset.slideIndex = String(slideIndex);
			const clickHandler = (event) => {
				event.preventDefault();
				this.scrollToSection(slideIndex);
			};
			const keyHandler = (event) => {
				const key = event.key || event.code;
				if (key === "Enter" || key === " " || key === "Space" || key === "Spacebar") {
					event.preventDefault();
					this.scrollToSection(slideIndex);
				}
			};
			item.addEventListener("click", clickHandler);
			item.addEventListener("keydown", keyHandler);
			this.listItemBindings.push({
				el: item,
				click: clickHandler,
				keydown: keyHandler,
			});
		});
	}

	unbindListItemNavigation() {
		if (!this.listItemBindings.length) {
			return;
		}
		this.listItemBindings.forEach((binding) => {
			if (binding.el && binding.click) {
				binding.el.removeEventListener("click", binding.click);
			}
			if (binding.el && binding.keydown) {
				binding.el.removeEventListener("keydown", binding.keydown);
			}
		});
		this.listItemBindings = [];
	}

	scrollToSection(targetIndex) {
		if (!this.mainTrigger || !this.panels.length) {
			return;
		}
		const totalSections = this.panels.length;
		const desiredIndex = this.clamp(targetIndex, 0, totalSections - 1);
		this.pendingScrollIndex = desiredIndex;
		const targetY = this.getScrollYForIndex(desiredIndex);
		if (!Number.isFinite(targetY)) {
			this.setActive(desiredIndex, true);
			return;
		}
		// Keep scroll behavior consistent with the main step scrolling (GSAP ScrollToPlugin),
		// and immediately sync UI state to the requested section.
		this.setActive(desiredIndex, true);
		this.scrollToY(targetY, desiredIndex);
		this.scheduleScrollFailsafe(desiredIndex);
	}

	updateGridOffset() {
		if (!this.gridEl) {
			return;
		}
		const cfg = SolutionScrollBlock.GRID_CONFIGS[this.solutionPage] || SolutionScrollBlock.GRID_CONFIGS.default;
		const leftHeight = this.leftCol ? this.leftCol.offsetHeight : 0;
		const avgPanelHeight = this.getAveragePanelContentHeight();
		const panelHeight = avgPanelHeight || this.getPanelContentHeight(this.lastActiveIndex > -1 ? this.lastActiveIndex : 0);
		const ctaHeight = this.ctaRail ? this.ctaRail.offsetHeight : 0;
		const contentHeight = Math.max(leftHeight, panelHeight) + ctaHeight;
		const viewport = this.getViewportHeight();
		const spare = viewport - contentHeight;
		const w = window.innerWidth;
		const minTop = w >= 1920 ? cfg.minTop1920 : w >= 1440 ? cfg.minTop1440 : cfg.minTop;
		const minBottom = cfg.minBottom;
		const half = spare > 0 ? Math.floor(spare / 2) : 0;
		const baseTop = spare > 0 ? Math.max(minTop, half) : minTop;
		const top = baseTop + cfg.extraTop;
		const bottom = spare > 0 ? Math.max(minBottom, half) : minBottom;
		this.gridEl.style.paddingTop = `${top}px`;
		this.gridEl.style.paddingBottom = `${bottom}px`;
	}

	scheduleGridOffsetUpdate() {
		if (!this.gridEl) {
			return;
		}
		if (this.gridOffsetRaf) {
			window.cancelAnimationFrame(this.gridOffsetRaf);
		}
		this.gridOffsetRaf = window.requestAnimationFrame(() => {
			this.gridOffsetRaf = null;
			this.updateGridOffset();
			this.updateOilLottieTop();
			this.updateGridTopLottieTop();
		});
	}

	initOilLottieTopTracking() {
		if (!this.root || !this.sectionsListEl || !this.oilLottieTopTargets || !this.oilLottieTopTargets.length) {
			return;
		}
		this.scheduleOilLottieTopUpdate();
		if (typeof ResizeObserver === "undefined") {
			return;
		}
		this.destroyOilLottieTopTracking();
		try {
			this.oilLottieTopObserver = new ResizeObserver(() => this.scheduleOilLottieTopUpdate());
			this.oilLottieTopObserver.observe(this.sectionsListEl);
		} catch (e) {
			this.oilLottieTopObserver = null;
		}
	}

	destroyOilLottieTopTracking() {
		if (this.oilLottieTopRaf) {
			window.cancelAnimationFrame(this.oilLottieTopRaf);
			this.oilLottieTopRaf = null;
		}
		if (this.oilLottieTopObserver) {
			try {
				this.oilLottieTopObserver.disconnect();
			} catch (e) { }
			this.oilLottieTopObserver = null;
		}
	}

	scheduleOilLottieTopUpdate() {
		if (!this.desktopInitialized || !this.sectionsListEl || !this.oilLottieTopTargets || !this.oilLottieTopTargets.length) {
			return;
		}
		if (this.oilLottieTopRaf) {
			window.cancelAnimationFrame(this.oilLottieTopRaf);
		}
		this.oilLottieTopRaf = window.requestAnimationFrame(() => {
			this.oilLottieTopRaf = null;
			this.updateOilLottieTop();
		});
	}

	updateOilLottieTop() {
		if (!this.desktopInitialized || !this.sectionsListEl || !this.oilLottieTopTargets || !this.oilLottieTopTargets.length) {
			return;
		}
		const listRect = this.sectionsListEl.getBoundingClientRect();
		if (!listRect || !Number.isFinite(listRect.bottom)) {
			return;
		}
		this.oilLottieTopTargets.forEach((el) => {
			if (!(el instanceof HTMLElement)) {
				return;
			}
			const offsetParent = el.offsetParent instanceof HTMLElement ? el.offsetParent : null;
			if (!offsetParent) {
				return;
			}
			const parentRect = offsetParent.getBoundingClientRect();
			const baseTop = listRect.bottom - parentRect.top + SolutionScrollBlock.OIL_LOTTIE_TOP_GAP_PX;
			const adjust = el.classList.contains("solutionScrollCarousel__decor-item--s2-oil-lottie") ? SolutionScrollBlock.OIL_S2_LOTTIE_TOP_ADJUST_PX : SolutionScrollBlock.OIL_S3_LOTTIE_TOP_ADJUST_PX;
			const top = Math.round(baseTop + adjust);
			el.style.top = `${top}px`;
		});
	}

	initGridTopLottieTracking() {
		if (!this.root || !this.gridEl || !this.leftCol || !this.gridTopLottieTargets || !this.gridTopLottieTargets.length) {
			return;
		}
		this.scheduleGridTopLottieUpdate();
		if (typeof ResizeObserver === "undefined") {
			return;
		}
		this.destroyGridTopLottieTracking();
		try {
			this.gridTopLottieObserver = new ResizeObserver(() => this.scheduleGridTopLottieUpdate());
			this.gridTopLottieObserver.observe(this.leftCol);
			this.gridTopLottieObserver.observe(this.gridEl);
		} catch (e) {
			this.gridTopLottieObserver = null;
		}
	}

	destroyGridTopLottieTracking() {
		if (this.gridTopLottieRaf) {
			window.cancelAnimationFrame(this.gridTopLottieRaf);
			this.gridTopLottieRaf = null;
		}
		if (this.gridTopLottieObserver) {
			try {
				this.gridTopLottieObserver.disconnect();
			} catch (e) { }
			this.gridTopLottieObserver = null;
		}
	}

	scheduleGridTopLottieUpdate() {
		if (!this.desktopInitialized || !this.gridEl || !this.leftCol || !this.gridTopLottieTargets || !this.gridTopLottieTargets.length) {
			return;
		}
		if (this.gridTopLottieRaf) {
			window.cancelAnimationFrame(this.gridTopLottieRaf);
		}
		this.gridTopLottieRaf = window.requestAnimationFrame(() => {
			this.gridTopLottieRaf = null;
			this.updateGridTopLottieTop();
		});
	}

	updateGridTopLottieTop() {
		if (!this.desktopInitialized || !this.gridEl || !this.leftCol || !this.gridTopLottieTargets || !this.gridTopLottieTargets.length) {
			return;
		}
		const gridStyle = window.getComputedStyle(this.gridEl);
		const gridPaddingTop = parseFloat(gridStyle.paddingTop) || 0;
		const leftHeight = this.leftCol.offsetHeight || 0;
		if (!leftHeight) {
			return;
		}
		const top = Math.round(gridPaddingTop + leftHeight);
		this.gridTopLottieTargets.forEach((el) => {
			if (!(el instanceof HTMLElement)) {
				return;
			}
			el.style.top = `${top}px`;
		});
	}

	getPanelContentHeight(index) {
		if (index < 0 || index >= this.panels.length) {
			return 0;
		}
		const panel = this.panels[index];
		if (!panel) {
			return 0;
		}
		const content = panel.querySelector(".solutionScrollCarousel__content");
		if (content) {
			const rect = content.getBoundingClientRect();
			if (rect && rect.height) {
				return rect.height;
			}
			return content.offsetHeight || 0;
		}
		const inner = panel.querySelector(".solutionScrollCarousel__panel-inner");
		return inner ? inner.offsetHeight : panel.offsetHeight || 0;
	}

	getAveragePanelContentHeight() {
		if (!this.panels.length) {
			return 0;
		}
		let sum = 0;
		let counted = 0;
		this.panels.forEach((panel, idx) => {
			const h = this.getPanelContentHeight(idx);
			if (h > 0) {
				sum += h;
				counted += 1;
			}
		});
		return counted ? sum / counted : 0;
	}

	getSectionSpan() {
		const base = this.clamp(this.getViewportHeight() * SolutionScrollBlock.SECTION_SCROLL_RATIO, SolutionScrollBlock.MIN_SECTION_SCROLL, SolutionScrollBlock.MAX_SECTION_SCROLL);
		return base * SolutionScrollBlock.SECTION_SCROLL_MULTIPLIER;
	}

	getDuration() {
		return this.getSectionSpan() * this.panels.length;
	}

	getExitBuffer() {
		const base = Math.round(this.getSectionSpan() * SolutionScrollBlock.EXIT_BUFFER_RATIO);
		return this.clamp(base, SolutionScrollBlock.EXIT_BUFFER_MIN, SolutionScrollBlock.EXIT_BUFFER_MAX);
	}

	getSceneStartOffset() {
		if (!this.mainTrigger) {
			return null;
		}
		const start = Number(this.mainTrigger.start);
		return Number.isFinite(start) ? start : null;
	}

	getDurationPx() {
		if (!this.mainTrigger) {
			return 0;
		}
		const start = Number(this.mainTrigger.start);
		const end = Number(this.mainTrigger.end);
		if (!Number.isFinite(start) || !Number.isFinite(end)) {
			return 0;
		}
		return Math.max(0, end - start);
	}

	getIndexFromProgress(progress) {
		const p = this.clamp(progress, 0, 1);
		const count = Math.max(1, this.panels.length);
		const span = 1 / count;
		const local = this.clamp(p, 0, 1 - SolutionScrollBlock.SECTION_PROGRESS_EPS);
		return Math.min(count - 1, Math.floor((local + SolutionScrollBlock.SECTION_PROGRESS_EPS) / span));
	}

	getViewportHeight() {
		return window.innerHeight || document.documentElement.clientHeight || 900;
	}

	getScrollY() {
		return window.pageYOffset || document.documentElement.scrollTop || 0;
	}

	clamp(value, min, max) {
		return Math.max(min, Math.min(max, value));
	}

	getElementIndex(el) {
		if (!el || !el.dataset) {
			return null;
		}
		const value = Number(el.dataset.index);
		return Number.isFinite(value) ? value : null;
	}

	findByIndex(collection, index) {
		if (!collection || !collection.length) {
			return null;
		}
		for (let i = 0; i < collection.length; i += 1) {
			if (this.getElementIndex(collection[i]) === index) {
				return collection[i];
			}
		}
		return null;
	}

	buildTitleLines(titleEl) {
		if (!titleEl || titleEl.dataset.linesBuilt === "1") {
			return;
		}
		titleEl.dataset.linesBuilt = "1";
		const original = titleEl.innerHTML;
		const container = document.createElement("span");
		container.style.display = "inline";
		titleEl.innerHTML = "";
		titleEl.appendChild(container);
		const tempRoot = document.createElement("span");
		tempRoot.style.display = "inline";
		tempRoot.innerHTML = original;
		container.appendChild(tempRoot);
		const words = [];
		const trailingSpaces = [];
		const wordTops = [];
		const walker = document.createTreeWalker(tempRoot, NodeFilter.SHOW_ALL);
		while (walker.nextNode()) {
			const n = walker.currentNode;
			if (n.nodeType === Node.ELEMENT_NODE && n.tagName === "BR") {
				words.push("__BR__");
				trailingSpaces.push("");
				wordTops.push(Number.NaN);
				continue;
			}
			if (n.nodeType === Node.TEXT_NODE) {
				const txt = n.nodeValue || "";
				if (!txt) {
					continue;
				}
				const regex = /(\S+)(\s*)/g;
				let m;
				while ((m = regex.exec(txt))) {
					const word = m[1];
					const spaces = m[2] || "";
					const r = document.createRange();
					r.setStart(n, m.index);
					r.setEnd(n, m.index + word.length);
					const rect = r.getBoundingClientRect();
					words.push(word);
					trailingSpaces.push(spaces);
					wordTops.push(Math.round(rect.top));
				}
			}
		}
		const lines = [];
		let currentTop = null;
		let currentLine = [];
		const tolerance = SolutionScrollBlock.TITLE_LINE_TOLERANCE;
		for (let i = 0; i < words.length; i += 1) {
			if (words[i] === "__BR__") {
				if (currentLine.length) {
					lines.push(currentLine);
					currentLine = [];
					currentTop = null;
				}
				continue;
			}
			const top = wordTops[i];
			if (currentTop === null) {
				currentTop = top;
				currentLine.push(i);
				continue;
			}
			if (Math.abs(top - currentTop) <= tolerance) {
				currentLine.push(i);
			} else {
				lines.push(currentLine);
				currentLine = [i];
				currentTop = top;
			}
		}
		if (currentLine.length) {
			lines.push(currentLine);
		}

		titleEl.innerHTML = "";
		for (let li = 0; li < lines.length; li += 1) {
			const lineWrapper = document.createElement("span");
			lineWrapper.style.display = "block";
			lineWrapper.style.overflow = "hidden";
			const lineEl = document.createElement("span");
			lineEl.className = "line";
			lineEl.style.display = "block";
			for (let wi = 0; wi < lines[li].length; wi += 1) {
				const idx = lines[li][wi];
				lineEl.appendChild(document.createTextNode(words[idx]));
				const trailing = trailingSpaces[idx];
				if (trailing && trailing.length) {
					lineEl.appendChild(document.createTextNode(" "));
				}
			}
			lineWrapper.appendChild(lineEl);
			titleEl.appendChild(lineWrapper);
		}

		try {
			const builtLines = Array.from(titleEl.querySelectorAll(".line"));
			if (builtLines.length) {
				gsap.set(builtLines, { yPercent: 100 });
			}
		} catch (e) { }
	}

	playTitleLines(titleEl, lineDelayMs = SolutionScrollBlock.TITLE_LINE_DELAY, startDelayMs = 0) {
		if (!titleEl) {
			return 0;
		}
		const lines = Array.from(titleEl.querySelectorAll(".line"));
		if (!lines.length) {
			return 0;
		}
		try {
			gsap.killTweensOf(lines);
			gsap.fromTo(
				lines,
				{ yPercent: 100 },
				{
					yPercent: 0,
					duration: 1,
					ease: "power4.out",
					delay: Math.max(0, startDelayMs) / 1000,
					stagger: Math.max(0, lineDelayMs) / 1000,
					overwrite: true,
				},
			);
		} catch (e) { }
		return startDelayMs + lines.length * lineDelayMs + 1000;
	}

	cancelLottieExit(container) {
		if (!container) {
			return;
		}
		if (container._sscExitHandler) {
			try {
				container.removeEventListener("transitionend", container._sscExitHandler);
			} catch (e) { }
			container._sscExitHandler = null;
		}
		if (container._sscExitTimer) {
			this.killDelay(container._sscExitTimer);
			container._sscExitTimer = null;
		}
		container.classList.remove("is-leaving-up");
		container.classList.remove("is-leaving-down");
	}

	destroyLottieContainer(container) {
		if (!container) {
			return;
		}
		this.cancelLottieExit(container);
		if (container._sscPlayOnVisibleTimer) {
			this.killDelay(container._sscPlayOnVisibleTimer);
			container._sscPlayOnVisibleTimer = null;
		}
		try {
			if (container._sscLottieInstance && typeof container._sscLottieInstance.destroy === "function") {
				container._sscLottieInstance.destroy();
			}
		} catch (e) { }
		container._sscLottieInstance = null;
		delete container.dataset.lottieStarted;
		try {
			container.innerHTML = "";
		} catch (e) { }
	}

	animateLottieExit(scope, direction) {
		if (!scope) {
			return;
		}
		const containers = Array.from(scope.querySelectorAll(".scrollCarouselLottie"));
		if (!containers.length) {
			return;
		}
		const leaveClass = direction === "reverse" ? "is-leaving-down" : "is-leaving-up";
		containers.forEach((container) => {
			this.cancelLottieExit(container);
			const handle = (event) => {
				if (event && event.target !== container) {
					return;
				}
				this.destroyLottieContainer(container);
			};
			container._sscExitHandler = handle;
			container.addEventListener("transitionend", handle);
			container.classList.add(leaveClass);
			container._sscExitTimer = this.delay(SolutionScrollBlock.LOTTIE_EXIT_DURATION, () => {
				this.destroyLottieContainer(container);
			});
		});
	}

	resetLotties(scope) {
		if (!scope) {
			return;
		}
		const containers = Array.from(scope.querySelectorAll(".scrollCarouselLottie"));
		containers.forEach((container) => this.destroyLottieContainer(container));
	}

	restartLotties(scope) {
		if (!scope) {
			return;
		}
		this.clearLottieWaitTimer(scope);
		this.resetLotties(scope);
		this.initLotties(scope);
	}

	clearLottieWaitTimer(scope) {
		if (!scope || !scope._sscLottieWaitTimer) {
			return;
		}
		this.killDelay(scope._sscLottieWaitTimer);
		scope._sscLottieWaitTimer = null;
	}

	initLotties(scope) {
		if (!scope) {
			return;
		}
		this.clearLottieWaitTimer(scope);
		const containers = Array.from(scope.querySelectorAll(".scrollCarouselLottie"));
		if (!containers.length) {
			return;
		}

		const start = () => {
			containers.forEach((container) => {
				if (container.dataset.lottieStarted === "1") {
					return;
				}
				const path = container.dataset.animationPath;
				if (!path) {
					return;
				}
				container.dataset.lottieStarted = "1";
				try {
					const shouldLoop = container.dataset.animationLoop === "true" || container.dataset.animationLoop === "1";
					const playFromStart = (anim) => {
						if (!anim) return;
						try {
							if (typeof anim.goToAndPlay === "function") {
								anim.goToAndPlay(0, true);
								return;
							}
						} catch (e) { }
						try {
							if (typeof anim.stop === "function") {
								anim.stop();
							}
							if (typeof anim.play === "function") {
								anim.play();
							}
						} catch (e) { }
					};

					const animation = window.lottie.loadAnimation({
						container,
						renderer: "svg",
						loop: shouldLoop,
						autoplay: shouldLoop,
						path,
					});
					container._sscLottieInstance = animation;
					const speedRaw = container.dataset.animationSpeed;
					const speed = Number(speedRaw);
					if (Number.isFinite(speed) && speed > 0 && animation && typeof animation.setSpeed === "function") {
						animation.setSpeed(speed);
					}

					// For non-loop animations: don't let them play while still hidden by reveal.
					if (!shouldLoop) {
						const fadeHost = container.closest(".js-fade-item");
						const isVisible = fadeHost ? fadeHost.classList.contains("is-visible") : true;
						if (isVisible) {
							playFromStart(animation);
						} else {
							let tries = 0;
							const check = () => {
								tries += 1;
								const nowVisible = fadeHost && fadeHost.classList.contains("is-visible");
								if (nowVisible) {
									container._sscPlayOnVisibleTimer = null;
									playFromStart(animation);
									return;
								}
								if (tries > 100) {
									container._sscPlayOnVisibleTimer = null;
									return;
								}
								container._sscPlayOnVisibleTimer = this.delay(50, check);
							};
							check();
						}
					}
				} catch (e) {
					delete container.dataset.lottieStarted;
					container._sscLottieInstance = null;
				}
			});
		};
		if (window.lottie && typeof window.lottie.loadAnimation === "function") {
			start();
		} else {
			let attempts = 0;
			const tick = () => {
				attempts += 1;
				if (window.lottie && typeof window.lottie.loadAnimation === "function") {
					this.clearLottieWaitTimer(scope);
					start();
					return;
				}
				if (attempts > 40) {
					this.clearLottieWaitTimer(scope);
					return;
				}
				scope._sscLottieWaitTimer = this.delay(150, tick);
			};
			tick();
		}
	}

	resetAllLotties(scope = this.root) {
		if (!scope) {
			return;
		}
		this.clearLottieWaitTimer(scope);
		const containers = Array.from(scope.querySelectorAll(".scrollCarouselLottie"));
		containers.forEach((container) => this.destroyLottieContainer(container));
	}

	clearElementReveal(el, className = "is-visible") {
		if (!el) {
			return;
		}
		if (this.revealTimers && this.revealTimers.has(el)) {
			const tween = this.revealTimers.get(el);
			this.killDelay(tween);
			this.revealTimers.delete(el);
		}
		el.classList.remove(className);
	}

	scheduleElementReveal(el, delay = 0, className = "is-visible") {
		if (!el) {
			return;
		}
		if (!this.revealTimers) {
			this.revealTimers = new Map();
		}
		this.clearElementReveal(el, className);
		const handle = this.delay(delay, () => {
			el.classList.add(className);
			if (this.revealTimers) {
				this.revealTimers.delete(el);
			}
		});
		this.revealTimers.set(el, handle);
	}

	clearAllRevealTimers() {
		if (!this.revealTimers || !this.revealTimers.size) {
			return;
		}
		this.revealTimers.forEach((timer) => this.killDelay(timer));
		this.revealTimers.clear();
	}

	clearScrollFailsafe() {
		if (this.pendingScrollCheckTimer) {
			this.killDelay(this.pendingScrollCheckTimer);
			this.pendingScrollCheckTimer = null;
		}
	}

	scheduleScrollFailsafe(targetIndex) {
		this.clearScrollFailsafe();
		const apply = () => {
			this.pendingScrollCheckTimer = null;
			if (this.lastActiveIndex !== targetIndex) {
				this.setActive(targetIndex, true);
				this.pendingScrollIndex = null;
			}
		};
		this.pendingScrollCheckTimer = this.delay(SolutionScrollBlock.SCROLL_FAILSAFE_MS, apply);
	}

	initVideoModals() {
		if (this.videoModalsInitialized) {
			return;
		}

		this.videoController = new VideoModalController({
			root: this.root,
			modalSelector: '.uk-modal[id^="solutionScrollCarousel-cta-"]',
			fallbackTimeoutMs: 12000,
			i18nDomain: "datarobot",
		});

		const attached = this.videoController.attach();
		if (!attached) {
			this.videoController = null;
		}

		this.videoModalsInitialized = true;
	}
}

document.addEventListener("DOMContentLoaded", () => {
	const root = document.querySelector(".solutionScrollCarousel");
	if (!root) {
		return;
	}
	root.solutionScrollBlock = new SolutionScrollBlock(root);
});

/* =============================================
   Mobile carousel (Swiper) – merged from solutionsCarousel.js
   ============================================= */

const MOBILE_DESKTOP_MIN_WIDTH = 1280;
let _scMobileViewportIsMobile = null;

function initMobileSolutionCarousel(root) {
	const thumbsEl = root.querySelector(".solutionCarousel__swiper-thumbs");
	const mainEl = root.querySelector(".solutionCarousel__swiper");
	const nextBtn = root.querySelector(".solutionCarousel__swiper-navigation-next");
	const paginationEl = root.querySelector(".solutionCarousel__pagination");

	if (!mainEl) return;

	// Prevent duplicate init
	if (root.dataset.scInit === "1") return;
	root.dataset.scInit = "1";

	if (mainEl.swiper && typeof mainEl.swiper.destroy === "function") {
		try { mainEl.swiper.destroy(true, true); } catch (e) { }
	}
	if (thumbsEl && thumbsEl.swiper && typeof thumbsEl.swiper.destroy === "function") {
		try { thumbsEl.swiper.destroy(true, true); } catch (e) { }
	}

	const thumbsSwiper = thumbsEl
		? new Swiper(thumbsEl, {
			loop: false,
			slidesPerView: 1,
			spaceBetween: 12,
			freeMode: true,
			watchSlidesVisibility: true,
			watchSlidesProgress: true,
			slideToClickedSlide: true,
			observer: true,
			observeParents: true,
			breakpoints: {
				768: { slidesPerView: 3 },
			},
		})
		: null;

	let mainSwiper = null;
	let isMobile = window.innerWidth < 768;

	function createMainSwiper() {
		if (mainSwiper && typeof mainSwiper.destroy === "function") {
			try { mainSwiper.destroy(true, true); } catch (e) { }
		}
		mainSwiper = new Swiper(mainEl, {
			loop: isMobile,
			loopAdditionalSlides: 1,
			effect: isMobile ? "slide" : "fade",
			fadeEffect: { crossFade: false },
			autoHeight: true,
			speed: 600,
			slidesPerView: 1,
			spaceBetween: 12,
			parallax: true,
			allowTouchMove: true,
			navigation: nextBtn ? { nextEl: nextBtn } : undefined,
			pagination: paginationEl ? { el: paginationEl, clickable: true } : undefined,
			thumbs: thumbsSwiper ? { swiper: thumbsSwiper } : undefined,
			observer: true,
			observeParents: true,
		});
		attachMainSync();
	}

	function attachMainSync() {
		if (!mainSwiper) return;
		mainSwiper.on("slideChange", function () {
			updateActiveThumb(mainSwiper.activeIndex);
			resetInactiveSlidesLottie();
			handleActiveSlideLottie();
		});
		mainSwiper.on("slideChangeTransitionEnd", function () {
			resetInactiveSlidesLottie();
			handleActiveSlideLottie();
			mainSwiper.updateAutoHeight();
		});
	}

	createMainSwiper();

	function handleActiveSlideLottie() {
		const active = mainEl.querySelector(".swiper-slide-active");
		if (!active) return;
		initLottieIfPresent(active);
	}

	function resetInactiveSlidesLottie() {
		const slides = Array.from(mainEl.querySelectorAll(".swiper-slide"));
		slides.forEach((slide) => {
			if (slide.classList.contains("swiper-slide-active")) return;
			const containers = Array.from(slide.querySelectorAll(".carouselLottieContainer"));
			containers.forEach((container) => {
				const isLoop = container.dataset.animationLoop === "true" || container.dataset.animationLoop === "1";
				if (isLoop) return;
				try {
					if (container._lottieAnimation && typeof container._lottieAnimation.destroy === "function") {
						container._lottieAnimation.destroy();
					}
				} catch (e) { }
				container._lottieAnimation = null;
				delete container.dataset.lottieStarted;
			});
		});
	}

	function initLottieIfPresent(contextEl) {
		if (!contextEl) return;
		if (!window.lottie || typeof window.lottie.loadAnimation !== "function") return;
		const containers = Array.from(contextEl.querySelectorAll(".carouselLottieContainer"));
		if (!containers.length) return;
		containers.forEach((container) => {
			if (container.dataset.lottieStarted === "1") return;
			container.dataset.lottieStarted = "1";
			const animationPath = container.dataset.animationPath;
			const shouldLoop = container.dataset.animationLoop === "true" || container.dataset.animationLoop === "1";
			if (!animationPath) return;
			try {
				const animation = window.lottie.loadAnimation({
					container: container,
					renderer: "svg",
					loop: shouldLoop,
					autoplay: true,
					path: animationPath,
				});
				container._lottieAnimation = animation;
				animation.addEventListener("DOMLoaded", () => {
					if (mainSwiper && typeof mainSwiper.updateAutoHeight === "function") {
						mainSwiper.updateAutoHeight(mainSwiper.params.speed);
					}
				});
				if (!shouldLoop) {
					const markPlayed = () => {
						try {
							if (animation && typeof animation.getDuration === "function" && typeof animation.goToAndStop === "function") {
								const totalFrames = animation.getDuration(true);
								const lastFrame = Math.max(0, Math.floor(totalFrames - 1));
								animation.goToAndStop(lastFrame, true);
								animation.pause();
							}
						} catch (e) { }
					};
					animation.addEventListener("complete", markPlayed);
					animation.addEventListener("data_failed", markPlayed);
				} else {
					animation.addEventListener("data_failed", () => {
						try {
							if (animation && typeof animation.destroy === "function") animation.destroy();
						} catch (e) { }
						container._lottieAnimation = null;
						delete container.dataset.lottieStarted;
					});
				}
			} catch (e) { }
		});
	}

	handleActiveSlideLottie();

	function updateActiveThumb(index) {
		if (!thumbsEl) return;
		const slides = Array.from(thumbsEl.querySelectorAll(".swiper-slide"));
		slides.forEach((el, i) => el.classList.toggle("swiper-slide-thumb-active", i === index));
	}

	if (thumbsSwiper) {
		updateActiveThumb(mainSwiper.activeIndex || 0);
	}

	let thumbsClickHandler = null;
	if (thumbsEl) {
		thumbsClickHandler = (e) => {
			const slideEl = e.target.closest(".swiper-slide");
			if (!slideEl) return;
			const slides = Array.from(thumbsEl.querySelectorAll(".swiper-slide"));
			const idx = slides.indexOf(slideEl);
			if (idx >= 0) mainSwiper.slideTo(idx);
		};
		thumbsEl.addEventListener("click", thumbsClickHandler);
	}

	let resizeTimer = null;
	const resizeHandler = () => {
		if (resizeTimer) window.clearTimeout(resizeTimer);
		resizeTimer = window.setTimeout(() => {
			const nowMobile = window.innerWidth < 768;
			if (nowMobile !== isMobile) {
				isMobile = nowMobile;
				createMainSwiper();
			}
		}, 150);
	};
	window.addEventListener("resize", resizeHandler);

	// Video modal controls (solutionCarousel-video- modals, used in mobile/swiper layout)
	const mobileVideoController = new VideoModalController({
		root: root,
		modalSelector: '.uk-modal[id^="solutionCarousel-video-"]',
		fallbackTimeoutMs: 12000,
		i18nDomain: "datarobot",
	});
	mobileVideoController.attach();

	root._scDestroy = () => {
		if (resizeTimer) { window.clearTimeout(resizeTimer); resizeTimer = null; }
		window.removeEventListener("resize", resizeHandler);
		if (thumbsEl && thumbsClickHandler) thumbsEl.removeEventListener("click", thumbsClickHandler);
		if (mainSwiper && typeof mainSwiper.destroy === "function") {
			try { mainSwiper.destroy(true, true); } catch (e) { }
		}
		if (thumbsSwiper && typeof thumbsSwiper.destroy === "function") {
			try { thumbsSwiper.destroy(true, true); } catch (e) { }
		}
		if (root.dataset) delete root.dataset.scInit;
		root._scDestroy = null;
	};
}

document.addEventListener("DOMContentLoaded", () => {
	const sections = Array.from(document.querySelectorAll(".solutionCarousel"));
	if (!sections.length) return;

	function initAllMobile() {
		sections.forEach((section) => {
			if (section.dataset && section.dataset.scInit === "1") return;
			initMobileSolutionCarousel(section);
		});
	}

	function destroyAllMobile() {
		sections.forEach((root) => {
			if (typeof root._scDestroy === "function") { root._scDestroy(); return; }
			const thumbsEl = root.querySelector(".solutionCarousel__swiper-thumbs");
			const mainEl = root.querySelector(".solutionCarousel__swiper");
			if (mainEl && mainEl.swiper && typeof mainEl.swiper.destroy === "function") {
				try { mainEl.swiper.destroy(true, true); } catch (e) { }
			}
			if (thumbsEl && thumbsEl.swiper && typeof thumbsEl.swiper.destroy === "function") {
				try { thumbsEl.swiper.destroy(true, true); } catch (e) { }
			}
			if (root.dataset) delete root.dataset.scInit;
		});
	}

	function evaluateViewportAndToggle() {
		const nowMobile = window.innerWidth <= MOBILE_DESKTOP_MIN_WIDTH;
		if (_scMobileViewportIsMobile === null) {
			_scMobileViewportIsMobile = nowMobile;
			if (nowMobile) initAllMobile();
			return;
		}
		if (nowMobile !== _scMobileViewportIsMobile) {
			_scMobileViewportIsMobile = nowMobile;
			if (nowMobile) initAllMobile();
			else destroyAllMobile();
		}
	}

	evaluateViewportAndToggle();
	window.addEventListener("resize", () => {
		if (window._scMobileResizeTimer) window.clearTimeout(window._scMobileResizeTimer);
		window._scMobileResizeTimer = window.setTimeout(evaluateViewportAndToggle, 150);
	});
});
